import 'regenerator-runtime/runtime';
import { NOT_FOUND, redirect } from 'redux-first-router';
import { fetchData, fetchCheckout } from './utils/async';
import { getCookie, deleteCookie, deleteAllCookies } from './utils/dom';

// Customer specific data is loaded in frontend.
// Generic data for all clients is SSR
const clientUpdate = (dispatch, getState) => {
	// Generate random information on page load
	dispatch({ type: 'RANDOM' });

	if (typeof window !== 'undefined') {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({
			event: 'PageView',
			values: {
				path: getState().location.pathname,
			},
		});
	}
};

const clientInit = async (dispatch, getState) => {
	clientUpdate(dispatch, getState);

	const { setup } = getState().environment;
	const { selection = {}, destorySelection = false } = getState().checkout;
	let selectionData = {};
	let token = null;

	// destory selection based on current status in checkout.
	if (destorySelection || (typeof document !== 'undefined' && getCookie('receiptShown'))) {
		dispatch({ type: 'DESTROY_SELECTION' });
		return dispatch({ type: 'START' });
	}

	if (selection.token) token = selection.token;
	if (!token && typeof document !== 'undefined') token = getCookie('selection');
	const selectionCache = JSON.parse(localStorage.getItem('selectionCache'));

	if (token === '1') {
		deleteCookie('selection');
		token = null;
	}

	if (token) {
		if (selectionCache) dispatch({ type: 'INIT_SELECTION_CACHE', initCache: selectionCache });

		// All other pages except for receipt
		selectionData = fetchCheckout('cartRetrieve', { id: token }, setup.country);

		try {
			selectionData = await selectionData;
		} catch (err) {
			console.log('An error occurred when trying to retrieve previous selection.', err);
			dispatch({ type: 'CHECKOUT_ERROR' });
		}

		// Is the order completed? Delete the token.
		if (selectionData?.data?.node?.id && !selectionData?.data?.node?.completedAt) {
			dispatch({ type: 'UPDATE_SELECTION', data: selectionData.data.node });
		} else {
			dispatch({ type: 'DESTROY_SELECTION' });
			deleteCookie('selection');
			token = null;
		}
	}

	// If selection is set to another country than default change country data to that of selection.
	if (selectionData.location && selectionData.location.country !== setup.country) {
		let whichCountry = selectionData.location.country;
		if (!setup.countries[whichCountry]) whichCountry = '0';
		const countryData = setup.countries[whichCountry];

		dispatch({ type: 'UPDATE_COUNTRY', country: countryData.country, market: countryData.market, pricelist: countryData.pricelist });
	}

	// Ensure that we have the product cache for the current market and pricelist.
	// We load this in asyncronously because it's needed for search and will be needed for other pages if not already included.
	await retrieveProductCache(dispatch, getState);

	if (token) {
		const { products: productCache, productIdLookup = {} } = getState().productCache[setup.market][setup.pricelist];
		dispatch({ type: 'REPLACE_SELECTION_CACHE', productCache, productIdLookup });
	}

	let favourites = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('favourites')) : false;
	if (favourites) {
		const productCache = getState().productCache[setup.market][setup.pricelist];
		favourites = favourites.filter((favourite) => productCache.products[favourite] !== undefined);
		dispatch({ type: 'UPDATE_FAVOURITES', favourites });
	}

	const paymentInfo = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('paymentInfo')) : false;
	if (paymentInfo) {
		if (selectionData?.selection?.items && selectionData.selection.items.length > 0 && paymentInfo?.additionalInformation && paymentInfo.additionalInformation.length > 0) {
			// Ensure custom product data is synced with basket.
			const additionalInformation = paymentInfo.additionalInformation.filter((item) => selectionData.selection.items.filter((sItem) => sItem.item === item.item).length > 0);
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo: { ...paymentInfo, additionalInformation } });
		} else if ((!token || !selectionData?.selection?.items || selectionData.selection.items.length === 0) && paymentInfo?.additionalInformation && paymentInfo.additionalInformation.length > 0) {
			// Remove all additional information as basket is empty.
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo: { ...paymentInfo, additionalInformation: [] } });
		} else {
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo });
		}
	}

	dispatch({ type: 'SITE_UPDATED' });

	return getState();
};

const retrieveProductCache = async (dispatch, getState) => {
	const { setup } = getState().environment;
	const { previewCode, country, market, pricelist } = setup;
	let productCache;

	if (getState().productCache[market] && getState().productCache[market][pricelist]) return getState().productCache[market][pricelist];

	try {
		const body = previewCode ? { previewCode } : {};
		productCache = await fetchData(`product-cache/${market}/${pricelist}/`, body);
	} catch (err) {
		console.log('DEBUG: product cache not loaded');
	}

	dispatch({ type: 'PRODUCT_CACHE', market, pricelist, setup, productCache });
	return dispatch({ type: 'UPDATE_COUNTRY', market, pricelist, country });
};


const languagePrefix = ''; // '/:language?';
const useLanguage = (str = '') => `${languagePrefix}${str}`;

export default {
	CLIENT_INIT: {
		thunk: async (dispatch, getState) => clientInit(dispatch, getState).catch((err) => console.log('DEBUG: error initiating client site data', err)),
	},
	RETRIEVE_PRODUCT_CACHE: {
		thunk: async (dispatch, getState) => (
			retrieveProductCache(dispatch, getState)
		),
	},
	STATIC: {
		path: '/static/:noop?',
	},
	REFERRAL: {
		path: '/ref',
		thunk: async (dispatch, getState, bag) => {
			const { location, environment } = getState();
			const { getParameters = {}, setup } = environment;
			const { hash = '' } = bag.action.payload;
			let path = '';
			let search = '';
			const goTo = Object.keys(getParameters).reduce((acc, param) => (param.toLowerCase() === 'goto' ? getParameters[param] : acc), '');
			if (goTo) path = decodeURIComponent(goTo).replace(/^\/+/, '');
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			if (path === '/' || path === '') return dispatch(redirect({ type: 'START', payload: { language: setup.language, path: '', search, hash } }));
			return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language: setup.language, path, search, hash } }));
		},
	},
	START: {
		path: useLanguage('/'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location, pageData } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: '' }), {}) : path;

			// best guess that it's start page otherwise cached...
			const body = environment.setup.previewCode ? { previewCode: environment.setup.previewCode } : {};
			const startPage = !pageData || !pageData.start ? fetchData('', body) : pageData.start;
			const { hash = '' } = bag.action.payload;
			let startPageData;
			clientUpdate(dispatch, getState);

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', currentHash });

			try {
				startPageData = await startPage;
			} catch (err) {
				console.log('DEBUG: start page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: startPageData, page: 'start' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'start' });
		},
	},
	CLEAR_COOKIES: {
		path: useLanguage('/clear-cookies'),
		thunk: async (dispatch) => {
			if (typeof document !== 'undefined') {
				deleteAllCookies();
				window.location = '/';
			}
			return dispatch(redirect({ type: 'START' }));
		},
	},
	SEARCH: {
		path: useLanguage('/search'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'search' }), {}) : path;
			const { hash = '' } = bag.action.payload;

			clientUpdate(dispatch, getState);

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', currentHash });

			if (location.query && Object.keys(location.query).length > 0 && Object.keys(location.query)[0] !== '') {
				dispatch({ type: 'SEARCH', text: Object.keys(location.query)[0] });
			}

			await retrieveProductCache(dispatch, getState);
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'search' });
		},
	},
	SELECTION: {
		path: useLanguage('/selection'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location, pageData } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection' }), {}) : path;

			const body = environment.setup.previewCode ? { previewCode: environment.setup.previewCode } : {};
			const selectionPage = !pageData || !pageData.selection ? fetchData('selection', body) : pageData.selection;
			let selectionPageData;
			clientUpdate(dispatch, getState);

			try {
				selectionPageData = await selectionPage;
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: selectionPageData, page: 'selection' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection' });
		},
	},
	/*
	LOG_IN: {
		path: useLanguage('/login'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'login' }), {}) : path;

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'login' });
		},
	},
	LOG_OUT: {
		path: useLanguage('/logout'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages, language } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'logout' }), {}) : path;

			// Remove token

			dispatch({ type: 'PAGE_LOADED', paths, pageType: 'logout' });
			return dispatch(redirect({ type: 'START', payload: { language, path: '/' } }));
		},
	},
	REGISTER: {
		path: useLanguage('/register'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'register' }), {}) : path;

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'register' });
		},
	},
	FORGOT: {
		path: useLanguage('/forgot'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'forgot' }), {}) : path;

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'forgot' });
		},
	},
	ACCOUNT: {
		path: useLanguage('/account/:path?'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages, language } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'forgot' }), {}) : path;

			if (true) { // no token
				// Redirect to login
				return dispatch(redirect({ type: 'LOG_IN', payload: { language, path: '/login' } }));
			}

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'account' });
		},
	},
	*/
	CATEGORY_PRODUCT_PAGE: {
		path: useLanguage('/:path+'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			/*
				USE CASES:
				/women/clothing/dresses/harper - Product
				/women/clothing/harper - Product
				/women/clothing - Category
				/en/women/clothing - Category with localization
				/sv/dam/kläder - Category with right localization but wrong locale - redirect to english version.
				/en/page
				/page
			*/
			const { location, environment, pageData = {} } = getState();
			const { setup, collectionsListing = {}, campaignsListing = {}, pressListing = {}, stockistListing = {} } = environment;
			const { hash = '', fakeUpdate = false } = bag.action.payload;
			let language = languagePrefix !== '' ? location.payload.language : false;
			// Clean any encoded URI characters
			let path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			let search = '';

			// Remove any hashes from path
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			// Remove any queries from path
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			// "language" is probably not a language but a path.
			if (!language && language.length > 2) {
				path = `${language}/${path}`;
				language = null;
			}

			if (fakeUpdate) return dispatch({ type: 'PAGE_LOADED', paths: path, pageType: 'category' });

			// Get content for site.
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			const page = pageData[path] || fetchData(path, body);
			let listing = null;
			let pageDataReturn = {};
			let listingDataReturn;
			clientUpdate(dispatch, getState);

			if (collectionsListing?.meta?.default?.uri === location.payload.path) {
				listing = getState()?.listing?.collections || fetchData('listing/collections', body);
			}
			if (campaignsListing?.meta?.default?.uri === location.payload.path) {
				listing = getState()?.listing?.campaigns || fetchData('listing/campaigns', body);
			}
			if (pressListing?.meta?.default?.uri === location.payload.path) {
				listing = getState()?.listing?.press || fetchData('listing/press', body);
			}
			if (stockistListing?.meta?.default?.uri === location.payload.path) {
				listing = getState()?.listing?.stockist || fetchData('listing/stockist', body);
			}

			try {
				[, pageDataReturn, listingDataReturn] = await Promise.all([retrieveProductCache(dispatch, getState), page, listing]);
			} catch (err) {
				console.log(`DEBUG: failed page data not retrieved. ${path}`, err);
			}

			if (pageDataReturn && pageDataReturn.is404) {
				dispatch({ type: NOT_FOUND });
				return dispatch({ type: 'PAGE_LOADED', paths: path, pageType: '404' });
			}

			if (pageDataReturn.paths) {
				if (language !== false && path !== pageDataReturn.paths[language] && !pageDataReturn.is404 && pageDataReturn.paths[language]) {
					return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language, path: pageDataReturn.paths[language], search, hash } }));
				}
			}

			dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: path });

			const currentHash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash: currentHash });

			if (['product', 'category'].indexOf(pageDataReturn.pageType) !== -1) {
				const hashFilter = [];
				const filterPath = pageDataReturn.pageType === 'product' ? path.slice(0, path.lastIndexOf('/')) : path;

				if (currentHash) {
					currentHash.slice(1).split('&').forEach((filter) => {
						const filterParts = filter.split('=');
						if (filterParts.length === 1) {
							hashFilter.push({ [filterParts[0]]: true });
						} else {
							hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
						}
					});
				}

				dispatch({ type: 'RESET_FILTER' });
				dispatch({ type: 'FILTER', filterArray: [{ categories: filterPath }, ...hashFilter], force: true, setup: environment.setup, currentPath: filterPath });
			}

			if (listingDataReturn) dispatch({ type: 'LISTING', data: listingDataReturn, listing: listingDataReturn.paths.replace('listing/', '') });
			return dispatch({ type: 'PAGE_LOADED', paths: pageDataReturn.paths || path, pageType: pageDataReturn.pageType || 'page' });
		},
	},
};
